import { boolVal, boolValOrUndef, nbVal, strVal, strValOrUndef } from "@paroi/data-formatters-lib";

export interface APITable {
  id: string;
  label: string;
  name: string;
  description?: string;
  columns: APITableColumn[];
}

export interface APITableColumn {
  label: string;
  name: string;
  type: string;
  length?: number;
  required: boolean;
  unique: boolean;
  default?: string | number | boolean;
  dataSource?: APIColumnDataSource;
}

export interface APIColumnDataSource {
  type: ColumnDataSourceType;
  config?: RelationColumnDataSourceConfig | APIColumnDataSourceConfig;
  data?: ColumnDataSourceKeyValueDataType[];
}

export type ColumnDataSourceType = "relation" | "key_value" | "api";

export interface ColumnDataSourceKeyValueDataType {
  key: string;
  value: string;
}

export interface APIColumnDataSourceConfig {
  url: string;
  method: APIMethodType;
  authType: "none" | "bearer" | "apiKey";
  authToken?: string;
  headers?: string[];
  queryParams?: string[];
  targetFields: string[];
}

export type APIMethodType = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RelationColumnDataSourceConfig {
  relationTable: string;
  relationTableColumn: string;
}

export function formatToTable(data: unknown): APITable {
  if (typeof data !== "object" || data === null) {
    throw new Error("Invalid data");
  }
  const validData = data as Record<string, unknown>;
  return {
    id: strVal(validData.id),
    label: strVal(validData.label),
    name: strVal(validData.name),
    description: strValOrUndef(validData.description),
    columns: Array.isArray(validData.schema) ? validData.schema.map(formatToColumn) : [],
  };
}

export type TableColumnTypes = Map<string, string>;

export function formatToTableColumnTypes(data: any): TableColumnTypes {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    throw new Error(`Invalid TableColumnTypes type '${typeof data}'`);
  }
  const columnTypesMap: TableColumnTypes = new Map();
  for (const [key, val] of Object.entries(data)) {
    const oldVal = columnTypesMap.get(key);
    if (!oldVal) columnTypesMap.set(key, strVal(val));
  }

  return columnTypesMap;
}

function formatToColumn(data: unknown): APITableColumn {
  if (typeof data !== "object" || data === null) {
    throw new Error("Invalid column data");
  }
  const validData = data as Record<string, unknown>;
  let defaultValue: string | number | boolean | undefined;
  if (validData.default !== undefined) {
    const val = validData.default;
    if (typeof val === "string") {
      defaultValue = strVal(val);
    } else if (typeof val === "number") {
      defaultValue = nbVal(val);
    } else if (typeof val === "boolean") {
      defaultValue = boolVal(val);
    }
  }
  return {
    label: strVal(validData.label),
    name: strVal(validData.name),
    type: strVal(validData.type),
    length: validData.length ? nbVal(Number(validData.length)) : undefined,
    required: boolValOrUndef(validData.required) ?? false,
    unique: boolValOrUndef(validData.unique) ?? false,
    default: defaultValue,
    dataSource: validData.data_source ? formatToColumnDataSource(validData.data_source) : undefined,
  };
}

function formatToColumnDataSource(data: unknown): APIColumnDataSource | undefined {
  if (typeof data !== "object" || data === null) {
    throw new Error("Invalid column data source");
  }
  if (Array.isArray(data)) return;
  const validData = data as Record<string, unknown>;
  let config: RelationColumnDataSourceConfig | APIColumnDataSourceConfig | undefined;
  if (validData.config) {
    if (typeof validData.config !== "object" || validData.config === null) {
      throw new Error("Invalid column data source config");
    }
    const rawConfig: any = validData.config;
    if ("api_url" in rawConfig) {
      config = {
        url: strVal(rawConfig.api_url).replace(/\?.*$/, ""),
        method: formatToAPIMethodType(rawConfig.http_method),
        headers: Array.isArray(rawConfig.headers) ? rawConfig.headers.map(strVal) : undefined,
        targetFields: Array.isArray(rawConfig.target_fields)
          ? rawConfig.target_fields.map(strVal)
          : undefined,
        queryParams: getQueryParams(rawConfig.api_url),
        authType: rawConfig.auth_type === "bearer" ? "bearer" : "none",
        authToken: strValOrUndef(rawConfig.auth_token),
      };
    } else {
      config = {
        relationTable: strVal(rawConfig.relation_table),
        relationTableColumn: strVal(rawConfig.relation_table_column),
      };
    }
  }
  return {
    type: formatTableType(validData.type),
    config,
    data:
      validData.data && Array.isArray(validData.data)
        ? validData.data.map((item) => ({
            key: strVal(item.key),
            value: strVal(item.value),
          }))
        : undefined,
  };
}

function formatToAPIMethodType(data: unknown): APIMethodType {
  if (typeof data !== "string") {
    throw new Error("Invalid API method type");
  }
  const validData = data as APIMethodType;
  if (!["GET", "POST", "PUT", "PATCH", "DELETE"].includes(validData)) {
    throw new Error("Invalid API method type");
  }
  return validData;
}

function formatTableType(data: unknown): ColumnDataSourceType {
  if (typeof data !== "string") {
    throw new Error("Invalid table type");
  }
  const validData = data as ColumnDataSourceType;
  if (!["relation", "key_value", "api"].includes(validData)) {
    throw new Error("Invalid table type");
  }
  return validData;
}

function getQueryParams(url: string): string[] {
  const params: string[] = [];
  const urlObj = new URL(url);
  urlObj.searchParams.forEach((value, key) => {
    params.push(`${key}=${value}`);
  });
  return params;
}
